<template>
    <v-dialog
        v-model="editCategoryNameDialogHandle"
        transition="dialog-bottom-transition"
        max-width="600px"
        @keydown.esc="closeEditCategoryNameDialog()"
        @click:outside="closeEditCategoryNameDialog()"
    >
        <v-form @submit.prevent.native="processEditUserGroup()">
            <v-card>
                <v-toolbar
                    color="primary"
                    dark
                    :class="dialogHeadingClasses()"
                >
                    <v-icon v-text="icons.mdiAccountEdit" class="mr-3"></v-icon>
                    {{ $gettext('Edit User Group') }}
                    <v-spacer></v-spacer>
                    <v-icon class="mr-2" @click.native.stop="closeEditCategoryNameDialog()" v-text="icons.mdiClose"></v-icon>
                    <v-icon @click.native.stop="processEditUserGroup()" v-text="icons.mdiContentSave"></v-icon>
                </v-toolbar>
                <v-divider></v-divider>
                <v-card-text class="mt-8">
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    :label="$gettext('User Group Name')"
                                    :placeholder="$gettext('User Group Name')"
                                    required
                                    v-model="editCategoryName"
                                    :error-messages="editCategoryNameErrors"
                                    outlined
                                    @input="$v.editCategoryName.$touch()"
                                    @blur="$v.editCategoryName.$touch()"
                                    autofocus
                                    clearable
                                ></v-text-field>

                                <v-select
                                    :label="$gettext('Choose the Internet Option - Rules mode')"
                                    :placeholder="$gettext('Choose the Internet Option - Rules mode')"
                                    required
                                    v-model="editInternetOption"
                                    :error-messages="editInternetErrors"
                                    outlined
                                    @input="$v.editInternetOption.$touch()"
                                    @blur="$v.editInternetOption.$touch()"
                                    :items="getOptionsForUserGroupInternet()"
                                    item-text="text"
                                    item-value="value"
                                >
                                </v-select>

                                <v-select
                                    :label="$gettext('Categories Profile')"
                                    :placeholder="$gettext('Categories Profile')"
                                    v-model="editProfile"
                                    :error-messages="editProfileErrors"
                                    outlined
                                    @input="$v.editProfile.$touch()"
                                    @blur="$v.editProfile.$touch()"
                                    :items="profiles"
                                    item-text="profile_name"
                                    item-value="profile_name"
                                >
                                </v-select>
                            </v-col>

                        </v-row>

                    </v-container>

                </v-card-text>
                <v-divider></v-divider>

                <v-card-actions color="secondary">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click.native.stop="closeEditCategoryNameDialog()">
                        {{ $gettext('Close') }}
                    </v-btn>
                    <v-btn color="primary" type="submit">
                        {{ $gettext('Save') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>

    </v-dialog>
</template>
<style type="text/scss">
.add-rule-radio-margin .v-input--selection-controls {
    margin-top: 0;
}


</style>

<script>

import { required } from 'vuelidate/lib/validators'

import MixinDialog from '@/mixins/MixinDialog'
import MixinIcon from '@/mixins/MixinIcon'
import Snackbar from '@/components/Snackbar'
import MixinConfigUsers from '@/mixins/database/MixinConfigUsers'
import MixinVuelidate from '@/mixins/MixinVuelidate'
import { mapWritableState } from 'pinia'
import { useCategoriesStore } from '@/stores/CategoriesStore'

export default {
    components: { Snackbar },
    mixins: [MixinDialog, MixinIcon, MixinConfigUsers, MixinVuelidate],
    props: {
        editCategoryNameDialog: {
            required: true,
            type: Boolean,
            default: false
        },
        inEditCategoryName: {
            required: true,
            default: ''
        },
        inEditInternetOption: {
            required: true,
            default: ''
        },
        inEditProfile: {
            required: true,
            default: ''
        },
    },

    watch: {
        // Mounted only fills in the dialog for the first time, the rest of the time it is filled by the watcher
        editCategoryNameDialog (newVal) {

            let self = this

            if (newVal) {
                self.editCategoryName = self.$props.inEditCategoryName
                self.editInternetOption = self.$props.inEditInternetOption
                self.editProfile = self.$props.inEditProfile
            } else {
                self.resetForm()
            }

        }
    },

    mounted () {
        let self = this
        // Mounted only fills in the dialog for the first time, the rest of the time it is filled by the watcher
        self.editCategoryName = self.$props.inEditCategoryName
        self.editInternetOption = self.$props.inEditInternetOption
        self.editProfile = self.$props.inEditProfile
        self.editCategoryNameDialogHandle = self.$props.editCategoryNameDialog

    },
    data () {
        return {
            editCategoryName: '',
            editInternetOption: '',
            editCategoryNameDialogHandle: '',
            editProfile: '',
        }
    },

    methods: {

        async processEditUserGroup () {

            let self = this
            self.$v.$touch()

            await self.$v.$invalid
            await self.$nextTick()
            await self.$nextTick() // safety net

            setTimeout(() => {

                if ( ! self.$v.$invalid) {

                    self.$emit('user-group-edited', self.editCategoryName, self.editInternetOption, self.editProfile)
                    self.closeEditCategoryNameDialog()

                }

            }, 100)

        },

        closeEditCategoryNameDialog () {
            let self = this
            self.$emit('close-edit-category-name-dialog', 'editCategoryNameDialog')
        },
        resetForm () {
            let self = this
            self.$v.$reset()
            self.editCategoryName = ''
            self.editInternetOption = ''
        },
    },
    computed: {

        editCategoryNameErrors () {

            const errors = []
            if ( ! this.$v.editCategoryName.$dirty) return errors
            ! this.$v.editCategoryName.required && errors.push(this.$gettext('User group name is required'))
            ! this.$v.editCategoryName.isUnique && errors.push(this.$gettext('We already have a user group with this name'))

            return errors
        },

        editInternetErrors () {
            const errors = []
            if ( ! this.$v.editInternetOption.$dirty) return errors
            ! this.$v.editInternetOption.required && errors.push(this.$gettext('Internet option is required'))
            return errors
        },

        editProfileErrors () {
            const errors = []
            if ( ! this.$v.editProfile.$dirty) return errors
            ! this.$v.editProfile.required && errors.push(this.$gettext('Categories profile is required'))
            return errors
        },

        ...mapWritableState(useCategoriesStore, {
            profiles: 'profiles'
        })

    },
    validations: {
        editCategoryName: {
            required,
            async isUnique (value, vm) {
                return await this.vuelidateIsUniqueForKeyValueType(value, this.users, vm.$props.inEditCategoryName)
            }
        },
        editInternetOption: {
            required
        },
        editProfile: {
            required
        }
    }

}
</script>
